import { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { VideoDisplay } from '../components/Videos/VideoViews/VideoDisplay';
import { Video } from '../interfaces/userInterfaces';
import { getVideoById } from '../services/fetch-utils';

// Handles two entry paths:
//   1. Via navigate() with router state (normal in-app click) — instant render
//   2. Direct URL visit /media/:id with no state  — fetches the video by ID

export const VideoMediaDisplay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [video, setVideo] = useState<Video | null>(location.state?.video ?? null);
  const [loading, setLoading] = useState(!location.state?.video);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Already have the video from router state — nothing to fetch
    if (location.state?.video) return;

    if (!id) {
      navigate('/');
      return;
    }

    const load = async () => {
      try {
        const res = await getVideoById(Number(id));
        if (res.status === 200 && res.data) {
          setVideo(res.data);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  if (loading)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 60px)',
          color: 'var(--text-muted)',
          fontSize: 14,
        }}
      >
        Loading video…
      </div>
    );

  if (error || !video)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 60px)',
          gap: 12,
        }}
      >
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Video not found.</p>
        <button className="btn-ghost" onClick={() => navigate('/')}>
          Go home
        </button>
      </div>
    );

  return <VideoDisplay {...video} />;
};
